import { Button, Stack, styled, Typography } from "~/common/mui";
import { C_SURFACE } from "~/common/components/icon/Colors";
import { Container } from "~/common/components/Layout";
import { type ChangeEvent, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { INVALID_PACKAGE_CODE } from "~/utils/constants";
import { getTotalPrice } from "~/domains/product/utils/productDescriptorUtils";
import { Permission, userHasPermission } from "~/domains/user/utils/permissions";
import { PSSizeMode, selectBestPackBasedOnStock } from "~/utils/cartHelper";
import { getInitialPriceReference } from "~/features/addToCart/redux/addToCart/getInitialPriceReference";
import type { PackagingInformationEntry } from "~/domains/product/types/ProductData";
import { isValidQuantity } from "~/utils/packageEntryUtils";
import { FrequentlyBoughtTogetherItem } from "~/features/productDetails/components/FrequentlyBoughtTogetherSection/FrequentlyBoughtTogetherItem/FrequentlyBoughtTogetherItem";
import AddIcon from "@mui/icons-material/Add";

import { text, volatileSum, zero } from "~/utils/PriceUtils";
import { addToCartThunk } from "~/features/selectionAssistant/lubrication/AddToCartThunk";
import { useTranslation } from "react-i18next";
import { sendEvent } from "~/app/analytics/ga/ga";
import { Alert } from "~/common/mui/Alert/Alert";
import { selectNullableBothProductPrices } from "~/utils/v2/priceSelectHelper";
import { bulkAddToWishlistThunk } from "~/features/wishlist/redux/wishlistSlice";
import type { RootState } from "~/app/store";
import type { AnyAction } from "~/app/store/types";
import type { ThunkAction } from "redux-thunk";
import BulkAddToWishlistFlow, {
  type BulkAddToWishlistFlowProps,
} from "~/features/orders/orderDetails/components/AddToWishlist/BulkAddToWishlistFlow";
import { openModal } from "~/common/utils/ModalService";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import { usePurchasePlace } from "~/app/analytics/ga/recommendations/RecommendationContexts";
import type { PurchaseType } from "~/app/analytics/ga/recommendations/types";
import { FrequentlyBoughtTogetherItemSkeleton } from "~/features/productDetails/components/FrequentlyBoughtTogetherSection/FrequentlyBoughtTogetherItem/FrequentlyBoughtTogetherItemSkeleton";
import { useIsMaxTabletSize } from "~/common/hooks/useMediaQuery";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import BookmarkIcon from "@mui/icons-material/Bookmark";

interface Props {
  className?: string;
  loading?: boolean;
  mainProduct?: ProductDescriptor;
  firstProduct?: ProductDescriptor;
  secondProduct?: ProductDescriptor;
}

interface TileContainerProps {
  hasSecondProduct?: boolean;
}

const Section = styled("section")(({ theme }) => ({
  background: C_SURFACE,
  paddingBlock: 16,
  "&.scha__pdp__frequently-bought-together": {
    width: "100vw",
    position: "relative",
    left: "50%",
    right: "50%",
    marginLeft: "-50vw",
    marginRight: "-50vw",
  },
  [theme.breakpoints.up("desktop-s")]: {
    paddingBlock: 24,
  },
}));

const LoadingContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 8,
}));

export const FrequentlyBoughtTogetherSection = ({
  loading,
  className,
  mainProduct,
  firstProduct,
  secondProduct,
}: Props) => {
  const isMaxTablet = useIsMaxTabletSize();
  const { t } = useTranslation();
  const noProducts = !firstProduct;

  const priceReference = getInitialPriceReference();
  const mainProductState = useProductState({ product: mainProduct, priceReference });
  const firstProductState = useProductState({ product: firstProduct, priceReference });
  const secondProductState = useProductState({ product: secondProduct, priceReference });
  const { sectionTrackingProps, trackAddToCart, trackSelectProduct } = useFrequentlyBoughtTogetherProductsTracking({
    mainProductState,
    firstProductState,
    secondProductState,
  });

  const recommendationPlace = usePurchasePlace();
  const recommendationType: PurchaseType = "fbt" as const;

  const productStates = [mainProductState, firstProductState, secondProductState];
  const makeOnSelect = (index: number) => (e: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    trackSelectProduct(index, checked);
    productStates[index].onSelect?.(e, checked);
  };

  const dispatch = useDispatch();

  if (!loading && noProducts) {
    return null;
  }

  let content;

  if (loading && noProducts) {
    content = (
      <SectionLayout {...sectionTrackingProps}>
        <Typography variant={"titleLarge"}>{t("feature.frequentlyBoughtTogether.title")}</Typography>
        <TileContainer hasSecondProduct={true}>
          <FrequentlyBoughtTogetherItemSkeleton />
          <AddIcon color={firstProductState.selected ? "primary" : "disabled"} />
          <FrequentlyBoughtTogetherItemSkeleton />
          <AddIcon color={!loading && secondProductState.selected ? "primary" : "disabled"} />
          <FrequentlyBoughtTogetherItemSkeleton />
        </TileContainer>
        <Summary />
      </SectionLayout>
    );
  } else {
    const selectedProductStates = [mainProductState, firstProductState, secondProductState].filter(
      ({ product, selected }) => !!product && selected,
    );
    const totalQuantity = selectedProductStates.reduce((acc, x) => acc + x.quantity, 0);

    let totalSumComponent = null;
    if (userHasPermission(Permission.PRICE)) {
      const totalValue = volatileSum(
        ...selectedProductStates.map(({ price, quantity }) => {
          if (!price) {
            return zero();
          }
          return getTotalPrice(price, quantity);
        }),
      );
      totalSumComponent = (
        <Stack direction={isMaxTablet ? "row" : "column"} gap={isMaxTablet ? 1 : 0}>
          <Typography variant={"titleLarge"} component={"b"}>
            {t("feature.frequentlyBoughtTogether.total")}
          </Typography>
          <Typography variant={"titleLarge"} component={"b"}>
            {text(totalValue, t("product.priceOnRequest"))}
          </Typography>
        </Stack>
      );
    }

    let button = null;
    const buttonDisabled =
      loading ||
      selectedProductStates.length === 0 ||
      selectedProductStates.some(({ product, invalidQuantity }) => !product || product.isLoading || invalidQuantity);

    if (userHasPermission(Permission.ADD_TO_CART)) {
      const label = t("feature.frequentlyBoughtTogether.addToCart", { count: loading ? 0 : totalQuantity });
      button = (
        <Button
          startIcon={<ShoppingCartIcon  />}
          onClick={() => {
            trackAddToCart();
            dispatch(
              addToCartThunk(
                selectedProductStates.map(({ product, quantity, packageCode, priceType }) => ({
                  productCode: product!.base.code,
                  quantity,
                  packCode: packageCode,
                  priceReference: getInitialPriceReference(),
                  saleEnabled: priceType === "salesPrice",
                })),
                undefined,
                {
                  page: recommendationPlace,
                  recommendationPlace,
                  recommendationType,
                },
              ),
            );
          }}
          disabled={buttonDisabled}
        >
          {label}
        </Button>
      );
    } else if (userHasPermission(Permission.ADD_TO_WISHLIST)) {
      const label = t("feature.frequentlyBoughtTogether.addToWishlist", { count: loading ? 0 : totalQuantity });
      button = (
        <Button
          startIcon={<BookmarkIcon  />}
          disabled={buttonDisabled}
          onClick={() => {
            openModal("addToWishlist", (close) => (
              <AddCartToWishlistPopupFlow
                onClose={close}
                items={selectedProductStates.filter(({ product }) => !!product) as AddToWishlistItem[]}
              />
            ));
          }}
        >
          {label}
        </Button>
      );
    }

    content = (
      <SectionLayout {...sectionTrackingProps}>
        <Typography variant={"titleLarge"}>{t("feature.frequentlyBoughtTogether.title")}</Typography>
        <TileContainer hasSecondProduct={!!secondProductState?.product}>
          <FrequentlyBoughtTogetherItem main {...mainProductState} onSelect={makeOnSelect(0)} />
          {mainProductState.invalidQuantity && <InvalidQuantityError column={1} />}
          <AddIcon color={firstProductState.selected ? "primary" : "disabled"} />
          <FrequentlyBoughtTogetherItem {...firstProductState} onSelect={makeOnSelect(1)} />
          {firstProductState.invalidQuantity && <InvalidQuantityError column={3} />}
          {secondProductState.product && (
            <>
              <AddIcon color={!loading && secondProductState.selected ? "primary" : "disabled"} />
              <FrequentlyBoughtTogetherItem loading={loading} {...secondProductState} onSelect={makeOnSelect(2)} />
              {secondProductState.invalidQuantity && <InvalidQuantityError column={5} />}
            </>
          )}
          <Summary hasSecondProduct={!!secondProductState?.product}>
            {totalSumComponent}
            {button}
          </Summary>
        </TileContainer>
      </SectionLayout>
    );
  }

  return (
    <Section className={className}>
      <Container>{content}</Container>
    </Section>
  );
};

const Summary = styled("div")<TileContainerProps>(({ theme, hasSecondProduct }) => ({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  alignSelf: "stretch",
  gap: 16,

  [theme.breakpoints.between("tablet", "desktop-s")]: {
    marginLeft: 0,
    display: "grid",
    gridTemplateColumns: "1fr 33.25%",
    alignItems: "center",
    alignSelf: "stretch",
  },

  [theme.breakpoints.up("desktop-s")]: {
    marginLeft: hasSecondProduct ? "16px" : "8px",
  },
}));

const SectionLayout = styled("div", {
  shouldForwardProp: () => true,
})(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: 16,

  [theme.breakpoints.up("tablet")]: {
    gap: 24,
  },
  [theme.breakpoints.up("desktop-s")]: {
    gap: 32,
  },
}));

const TileContainer = styled("div")<TileContainerProps>(({ theme, hasSecondProduct }) => ({
  display: "flex",
  flexDirection: "column",
  gap: 8,
  alignItems: "center",

  [theme.breakpoints.up("tablet")]: {
    gridColumn: "1 / span 2",
  },

  [theme.breakpoints.up("desktop-s")]: {
    gridColumn: "1 / span 1",
    display: "grid",
    gridTemplateColumns: hasSecondProduct
      ? "minmax(0, 1fr) auto minmax(0, 1fr) auto minmax(0, 1fr) minmax(0, 1fr)"
      : "minmax(0, 1fr) auto minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)",
  },
}));

function useProductState({ priceReference, product }: { product?: ProductDescriptor; priceReference: string }) {
  const productCode = product?.base.code ?? "";
  const [packaging, setPackaging] = useState<PackagingInformationEntry | null>(null);
  const [quantity, setQuantity] = useState<number>(1);
  const [selected, setSelected] = useState(true);

  const packCode = packaging?.packCode ?? INVALID_PACKAGE_CODE;

  useEffect(() => {
    if (userHasPermission(Permission.AVAILABILITY)) {
      if (product && product.availability) {
        const packaging = selectBestPackBasedOnStock(1, null, product, PSSizeMode.SMALLEST_FIRST);
        if (packaging) {
          setPackaging(packaging);
          setQuantity(packaging.packSize);
        } else {
          setPackaging(null);
          setQuantity(1);
        }
      }
    }
  }, [product]);

  const { currentPrice: price, currentPriceType: priceType } = useSelector(
    selectNullableBothProductPrices({
      productCode,
      packCode,
      priceReference,
      extraQuantity: quantity,
    }),
  );

  let invalidQuantity = false;
  if (packaging) {
    invalidQuantity = !isValidQuantity(packaging.packSize, quantity);
  }

  return {
    product,
    price,
    priceType,
    invalidQuantity,
    selected: product ? selected : false,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onSelect: (_: any, checked: boolean) => setSelected(checked),
    quantity,
    onQuantityChange: setQuantity,
    packageCode: packCode,
  };
}

type ProductState = ReturnType<typeof useProductState>;
type ProductStateWithProduct = Omit<ProductState, "product"> & { product: ProductDescriptor };
function hasProduct(productState: ProductState): productState is ProductStateWithProduct {
  return !!productState.product;
}
type TrackingInputData = {
  mainProductState: ProductState;
  firstProductState: ProductState;
  secondProductState: ProductState;
};
interface TrackingData {
  sectionTrackingProps: Record<string, string>;
  trackSelectProduct: (index: number, newValue: boolean) => void;
  trackAddToCart: () => void;
}
export const useFrequentlyBoughtTogetherProductsTracking = ({
  mainProductState,
  firstProductState,
  secondProductState,
}: TrackingInputData): TrackingData => {
  const mainProduct = mainProductState.product;
  const salesCampaign =
    mainProduct?.base.salesCampaign &&
    mainProduct?.base.salesCampaign[mainProductState.packageCode] &&
    mainProduct?.base.salesCampaign[mainProductState.packageCode].length > 0
      ? mainProduct?.base.salesCampaign[mainProductState.packageCode][0].code
      : undefined;

  const sectionTrackingProps = {
    "tracking-aux-fbt-parent-marker": "true",
    "tracking-aux-fbt-parent-product-name": mainProduct?.base.designation ?? "",
    "tracking-aux-fbt-parent-product-id": mainProduct?.base.sapMaterialNumber ?? "",
    "tracking-aux-fbt-is-parent-product-campaign-belonging": salesCampaign ? "true" : "false",
    "tracking-aux-fbt-is-parent-product-campaign": salesCampaign ?? "None",
  };

  const products = [mainProductState, firstProductState, secondProductState];

  function mapProductData(state: ProductStateWithProduct, includeQuantity = false) {
    const product = state.product;
    const salesCampaign =
      product.base.salesCampaign &&
      product.base.salesCampaign[state.packageCode] &&
      product.base.salesCampaign[state.packageCode].length > 0
        ? product.base.salesCampaign[state.packageCode][0].code
        : undefined;

    const data: Record<string, string | number> = {
      name: product.base.designation,
      id: product.base.sapMaterialNumber,
      is_campaign_belonging: salesCampaign ? "true" : "false",
      campaign: salesCampaign ?? "None",
      price: state.price ? text(state.price) : "",
    };

    if (includeQuantity) {
      data.quantity = state.quantity;
    }

    return data;
  }

  const trackSelectProduct = (index: number, newValue: boolean) => {
    const product = products[index];
    if (hasProduct(product)) {
      sendEvent({
        event: newValue ? "fbt_product_selected" : "fbt_product_deselected",
        product_data: mapProductData(product),
      });
    }
  };

  const trackAddToCart = () => {
    const eventProducts = products
      .filter((product) => product.selected)
      .filter(hasProduct)
      .map((state) => mapProductData(state, true));
    if (eventProducts.length > 0) {
      sendEvent({
        event: "fbt_products_added_to_cart",
        products: eventProducts,
      });
    }
  };

  return {
    sectionTrackingProps,
    trackSelectProduct,
    trackAddToCart,
  };
};

const InvalidQuantityError = ({ column }: { column: number }) => {
  const { t } = useTranslation();
  return (
    <Alert severity={"error"} className={"w-100"} style={{ gridRow: 2, gridColumn: column }}>
      <Stack gap={0.5}>
        <b>{t("feature.frequentlyBoughtTogether.invalidQty.title")}</b>
        <span>{t("feature.frequentlyBoughtTogether.invalidQty.subtitle")}</span>
      </Stack>
    </Alert>
  );
};

type AddToWishlistItem = { product: ProductDescriptor; quantity: number };
type ThunkResult<R> = ThunkAction<R, RootState, undefined, AnyAction>;
function addToWishlistThunk(
  wishlistData: { wishlistId: string } | { wishlistName: string },
  items: AddToWishlistItem[],
): ThunkResult<Promise<string>> {
  const qtyPerCode = {} as Record<string, number>;
  items.forEach(
    (item) => (qtyPerCode[item.product.base.code] = item.quantity + (qtyPerCode[item.product.base.code] || 0)),
  );

  const wishlistItems = Object.entries(qtyPerCode).map((data) => ({
    code: data[0],
    quantity: data[1],
  }));

  return bulkAddToWishlistThunk({ ...wishlistData, products: wishlistItems });
}
type AddCartToWishlistPopupFlowProps = Pick<BulkAddToWishlistFlowProps, "onClose"> & {
  items: AddToWishlistItem[];
};
function AddCartToWishlistPopupFlow({ items, onClose }: AddCartToWishlistPopupFlowProps) {
  const dispatch = useDispatch();

  const handleAddToWishlist = (data: { wishlistId: string } | { wishlistName: string }) => {
    return dispatch(addToWishlistThunk(data, items));
  };

  return <BulkAddToWishlistFlow onClose={onClose} onAddToWishlist={handleAddToWishlist} />;
}
